import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import {
  Disc3, AlertCircle, AlertTriangle, Info, Copy, RotateCcw, Calculator, CheckCircle2, XCircle,
  ArrowRight, Gauge, Ruler,
} from 'lucide-react';
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend,
  ResponsiveContainer, ReferenceArea,
} from 'recharts';
import {
  TYRES, TYRE_FIELD_SPECS, GLOBAL_FIELD_SPECS, DYNAMIC_FACTOR_SPEC, DRIVELINE_FIELD_SPECS,
  DEFAULT_INPUTS, PRESETS, KMPH_TO_MPH, validateField, parseTyreSize, formatTyreSize,
  sizeSegments, explainPart, PART_TITLES, speedRpmPrefillUrl, gearRatioPrefillUrl,
} from '../../lib/tyreSizeTools';
import { tyreSizeToolApi } from '../../lib/toolsApi';
import {
  SERIES_COLORS, CHART_GRID, CHART_AXIS, AXIS_TICK_STYLE, AXIS_LABEL_STYLE, LINE_PROPS,
  TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE, LEGEND_STYLE,
} from './simulator/chartTheme';
import ChartZoomHeader from './simulator/ChartZoomHeader';
import { useChartZoom } from './simulator/useChartZoom';
import { SideView, CrossSection, SpeedoGauge } from './tyreSize/TyreDiagram';

const CALC_DEBOUNCE_MS = 350;
const GLOBAL_SPEC_BY_KEY = Object.fromEntries(GLOBAL_FIELD_SPECS.map((s) => [s.key, s]));
const DRIVELINE_SPEC_BY_KEY = Object.fromEntries(DRIVELINE_FIELD_SPECS.map((s) => [s.key, s]));
const FIELD_SHORT_LABEL = { WidthMm: 'Width', AspectPct: 'Aspect', RimIn: 'Rim' };
const FIELD_PROP = { WidthMm: 'widthMm', AspectPct: 'aspectPct', RimIn: 'rimIn' };

const fmt = (n, d = 2) => (n === null || n === undefined || Number.isNaN(n) ? '—' : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));
const fmtSigned = (n, d = 2) => (n === null || n === undefined || Number.isNaN(n) ? '—' : `${n > 0 ? '+' : n < 0 ? '−' : '±'}${fmt(Math.abs(n), d)}`);
const toUnit = (kmph, unit) => (unit === 'mph' ? kmph * KMPH_TO_MPH : kmph);
const unitLabel = (unit) => (unit === 'mph' ? 'mph' : 'km/h');

const tyreToStrings = (t) => ({
  widthMm: String(t.widthMm), aspectPct: String(t.aspectPct), rimIn: String(t.rimIn),
  zr: Boolean(t.zr), loadIndex: t.loadIndex ?? null, speedSymbol: t.speedSymbol ?? null,
});

const initialTyres = () => ({ orig: tyreToStrings(DEFAULT_INPUTS.orig), new: tyreToStrings(DEFAULT_INPUTS.new) });
const initialSizeText = (tyres) => ({ orig: formatTyreSize(tyres.orig), new: formatTyreSize(tyres.new) });
const initialRaw = () => ({
  speedoOverReadPct: String(DEFAULT_INPUTS.speedoOverReadPct),
  annualDistanceKm: String(DEFAULT_INPUTS.annualDistanceKm),
  cruiseSpeedKmph: String(DEFAULT_INPUTS.cruiseSpeedKmph),
  dynamicFactor: String(DEFAULT_INPUTS.dynamicFactor),
  gearRatio: '',
  finalDriveRatio: '',
});

const FieldLabel = ({ children, unit, tip, htmlFor }) => (
  <div className="flex items-center gap-1.5">
    <Label htmlFor={htmlFor} className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
      {children} {unit && <span className="normal-case text-muted-foreground/70">({unit})</span>}
    </Label>
    {tip && (
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="text-muted-foreground/70" aria-label={`About ${typeof children === 'string' ? children : 'this field'}`}>
            <Info className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{tip}</TooltipContent>
      </Tooltip>
    )}
  </div>
);

const FieldError = ({ error, testId }) => (error ? (
  <p className="flex items-center gap-1 text-xs text-destructive" data-testid={testId}>
    <AlertCircle className="h-3 w-3 shrink-0" /> {error}
  </p>
) : null);

const TyreSizeCalculator = () => {
  const [tyres, setTyres] = useState(initialTyres);
  const [sizeText, setSizeText] = useState(() => initialSizeText(initialTyres()));
  const [parseErrors, setParseErrors] = useState({ orig: null, new: null });
  const [sizeFocused, setSizeFocused] = useState(null);
  const [raw, setRaw] = useState(initialRaw);
  const [radiusBasis, setRadiusBasis] = useState(DEFAULT_INPUTS.radiusBasis);
  const [speedUnit, setSpeedUnit] = useState('kmph');
  const [preset, setPreset] = useState(PRESETS[0].key);
  const [hl, setHl] = useState({ hover: null, focus: null, pinned: null });

  const setField = (key, value) => setRaw((prev) => ({ ...prev, [key]: value }));

  const setTyreField = (prefix, prop, value) => {
    const updated = { ...tyres[prefix], [prop]: value };
    setTyres((prev) => ({ ...prev, [prefix]: updated }));
    setSizeText((st) => ({ ...st, [prefix]: formatTyreSize(updated) }));
    setParseErrors((pe) => ({ ...pe, [prefix]: null }));
    setPreset('custom');
  };

  const onSizeText = (prefix, text) => {
    setSizeText((st) => ({ ...st, [prefix]: text }));
    const parsed = parseTyreSize(text);
    if (parsed.ok) {
      setTyres((prev) => ({ ...prev, [prefix]: tyreToStrings(parsed) }));
      setParseErrors((pe) => ({ ...pe, [prefix]: null }));
      setPreset('custom');
    } else {
      setParseErrors((pe) => ({ ...pe, [prefix]: parsed.empty ? null : parsed.error }));
    }
  };

  const applyPreset = (key) => {
    setPreset(key);
    const p = PRESETS.find((x) => x.key === key);
    if (!p) return;
    const next = { orig: tyreToStrings(p.orig), new: tyreToStrings(p.new) };
    setTyres(next);
    setSizeText(initialSizeText(next));
    setParseErrors({ orig: null, new: null });
  };

  const handleReset = () => {
    const t = initialTyres();
    setTyres(t);
    setSizeText(initialSizeText(t));
    setParseErrors({ orig: null, new: null });
    setRaw(initialRaw());
    setRadiusBasis(DEFAULT_INPUTS.radiusBasis);
    setSpeedUnit('kmph');
    setPreset(PRESETS[0].key);
    setHl({ hover: null, focus: null, pinned: null });
  };

  // Linked highlighting: hover wins over keyboard focus, which wins over a tap-pinned part.
  // A tap clears hover and focus too: touch browsers emulate mouseenter without mouseleave,
  // and tapping a chip also focuses it, so either would keep a toggled-off part lit.
  const onPart = (kind, tyre, part) => {
    const v = part ? { tyre, part } : null;
    setHl((prev) => {
      if (kind === 'hover') return { ...prev, hover: v };
      if (kind === 'leave') return { ...prev, hover: null };
      if (kind === 'focus') return { ...prev, focus: v };
      if (kind === 'blur') return { ...prev, focus: null };
      if (kind === 'tap') {
        const same = prev.pinned && prev.pinned.tyre === tyre && prev.pinned.part === part;
        return { hover: null, focus: null, pinned: same ? null : v };
      }
      return prev;
    });
  };
  const active = hl.hover || hl.focus || hl.pinned;

  const { fieldErrors, payload } = useMemo(() => {
    const errors = {};
    const body = { radiusBasis };
    TYRES.forEach(({ prefix }) => {
      TYRE_FIELD_SPECS.forEach((spec) => {
        const key = `${prefix}${spec.key}`;
        const value = tyres[prefix][FIELD_PROP[spec.key]];
        const err = validateField(spec, value);
        if (err) errors[key] = err;
        else body[key] = Number(value);
      });
      body[`${prefix}Zr`] = tyres[prefix].zr;
      body[`${prefix}LoadIndex`] = tyres[prefix].loadIndex;
      body[`${prefix}SpeedSymbol`] = tyres[prefix].speedSymbol;
    });
    GLOBAL_FIELD_SPECS.forEach((spec) => {
      const err = validateField(spec, raw[spec.key]);
      if (err) errors[spec.key] = err;
      else body[spec.key] = Number(raw[spec.key]);
    });
    if (radiusBasis === 'dynamic') {
      const err = validateField(DYNAMIC_FACTOR_SPEC, raw.dynamicFactor);
      if (err) errors.dynamicFactor = err;
      else body.dynamicFactor = Number(raw.dynamicFactor);
    }
    if (raw.gearRatio !== '' || raw.finalDriveRatio !== '') {
      DRIVELINE_FIELD_SPECS.forEach((spec) => {
        const err = validateField(spec, raw[spec.key]);
        if (err) errors[spec.key] = err;
        else body[spec.key] = Number(raw[spec.key]);
      });
    }
    return { fieldErrors: errors, payload: body };
  }, [tyres, raw, radiusBasis]);
  const hasErrors = Object.keys(fieldErrors).length > 0;

  const [results, setResults] = useState(null);
  const [calcError, setCalcError] = useState(null);
  const requestIdRef = useRef(0);

  const runCalculation = () => {
    if (hasErrors) return;
    const requestId = ++requestIdRef.current;
    tyreSizeToolApi.calculate(payload)
      .then((outcome) => {
        if (requestId !== requestIdRef.current) return;
        if (outcome.errors) {
          setResults(null);
          setCalcError(Object.values(outcome.errors)[0]);
        } else {
          setResults(outcome.results);
          setCalcError(null);
        }
      })
      .catch((e) => {
        if (requestId !== requestIdRef.current) return;
        setResults(null);
        setCalcError(e.message);
      });
  };

  useEffect(() => {
    if (hasErrors) { setResults(null); setCalcError(null); return undefined; }
    const timer = setTimeout(runCalculation, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasErrors, payload]);

  const handleCopy = async () => {
    if (!results) return;
    const u = unitLabel(speedUnit);
    const rows = results.speedoTable[speedUnit];
    const lines = [
      'Tyre Size & Speedometer Error Calculator',
      `Original: ${results.original.sizeLabel} — diameter ${fmt(results.original.diameterMm, 1)} mm, ${fmt(results.original.revsPerKm, 1)} rev/km`,
      `New:      ${results.new.sizeLabel} — diameter ${fmt(results.new.diameterMm, 1)} mm, ${fmt(results.new.revsPerKm, 1)} rev/km`,
      `Diameter change: ${fmtSigned(results.comparison.diameterDiffMm, 1)} mm (${fmtSigned(results.comparison.diameterDiffPct, 2)} %), ride height ${fmtSigned(results.comparison.groundClearanceChangeMm, 1)} mm`,
      `Speedometer error: ${fmtSigned(results.speedoErrorPct, 2)} % (${results.speedoErrorPct < 0 ? 'under-reads' : results.speedoErrorPct > 0 ? 'over-reads' : 'exact'}), factory over-read ${fmt(results.speedoOverReadPct, 1)} %`,
      `UNECE R39: ${results.r39.anyFail ? 'FAIL' : 'PASS'}`,
      '',
      `Indicated (${u}) | True (${u}) | Difference (${u}) | Error % | R39`,
      ...rows.map((r) => `${fmt(speedUnit === 'mph' ? r.indicatedMph : r.indicatedKmph, 0)} | ${fmt(toUnit(r.trueKmph, speedUnit), 1)} | ${fmtSigned(toUnit(r.differenceKmph, speedUnit), 1)} | ${fmtSigned(r.errorPct, 2)} | ${r.r39Pass ? 'PASS' : 'FAIL'}`),
      '',
      `Odometer: per 10,000 km shown, actual ${fmt(results.odometer.per10000Km.actualKm, 0)} km`,
    ];
    if (results.driveline) {
      lines.push(`RPM at ${fmt(results.driveline.cruiseSpeedKmph, 0)} km/h: ${fmt(results.driveline.rpmOriginal, 0)} → ${fmt(results.driveline.rpmNew, 0)} rpm; equivalent final drive ${fmt(results.driveline.equivalentFinalDrive, 3)}`);
    }
    results.warnings.forEach((w) => lines.push(`Warning: ${w.message}`));
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      toast.success('Results copied to clipboard.');
    } catch {
      toast.error('Could not copy to clipboard.');
    }
  };

  const globalField = (key, { tip, spec = GLOBAL_SPEC_BY_KEY[key] || DRIVELINE_SPEC_BY_KEY[key], label, optional = false, placeholder } = {}) => {
    const id = `tyre-size-${key}`;
    return (
      <div className="space-y-2" key={key}>
        <FieldLabel htmlFor={id} unit={spec.unit} tip={tip}>
          {label || spec.label}{optional && <span className="text-muted-foreground/60 normal-case"> (optional)</span>}
        </FieldLabel>
        <Input id={id} type="number" step="any" inputMode="decimal" value={raw[key]} onChange={(e) => setField(key, e.target.value)}
          className="font-mono" placeholder={placeholder} aria-invalid={!!fieldErrors[key]} data-testid={`${id}-input`} />
        <FieldError error={fieldErrors[key]} testId={`${id}-error`} />
      </div>
    );
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6" aria-label="Tyre Size and Speedometer Error Calculator">
        <Card className="technical-card">
          <CardHeader>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
                  <Disc3 className="h-5 w-5 text-autoxium-blue" />
                  Tyre Size &amp; Speedometer Error Calculator
                </CardTitle>
                <CardDescription>
                  Compare an original and a replacement tyre size: geometry, speedometer and odometer
                  error with a UNECE R39 check, and the new rolling radius.
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={preset} onValueChange={applyPreset}>
                  <SelectTrigger className="w-full sm:w-72 h-9" data-testid="tyre-size-preset-select" aria-label="Preset">
                    <SelectValue placeholder="Presets" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRESETS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                    <SelectItem value="custom" disabled>Custom</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="sm" onClick={handleCopy} disabled={!results} data-testid="tyre-size-copy-btn">
                  <Copy className="h-3.5 w-3.5" /> Copy Results
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        <div className="grid lg:grid-cols-5 gap-6">
          <Card className="technical-card lg:col-span-3">
            <CardHeader>
              <CardTitle className="font-chivo uppercase tracking-tight text-base">Tyre Sizes &amp; Options</CardTitle>
              <CardDescription>
                Type a sidewall size (e.g. <span className="font-mono">225/45 R17 94V</span>) or edit the fields. Hover, focus or
                tap any part of the size to see it highlighted on the diagrams below.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                {TYRES.map(({ prefix, label }) => (
                  <div key={prefix} className="p-4 bg-muted/50 rounded-md space-y-3" data-testid={`tyre-size-panel-${prefix}`}>
                    <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                      <span aria-hidden="true" className={prefix === 'new' ? 'inline-block w-5 border-t-2 border-primary' : 'inline-block w-5 border-t-2 border-dashed border-muted-foreground'} />
                      {label} Tyre
                    </h3>
                    <div className="space-y-2">
                      <FieldLabel htmlFor={`tyre-size-${prefix}-string`}>Size (as on sidewall)</FieldLabel>
                      <Input
                        id={`tyre-size-${prefix}-string`}
                        value={sizeText[prefix]}
                        onChange={(e) => onSizeText(prefix, e.target.value)}
                        onFocus={() => setSizeFocused(prefix)}
                        onBlur={() => setSizeFocused(null)}
                        className="font-mono"
                        autoComplete="off"
                        spellCheck={false}
                        aria-invalid={!!parseErrors[prefix] && sizeFocused !== prefix}
                        aria-describedby={`tyre-size-${prefix}-segments`}
                        data-testid={`tyre-size-${prefix}-string-input`}
                      />
                      <SegmentChips prefix={prefix} tyre={tyres[prefix]} active={active} onPart={onPart} />
                      {parseErrors[prefix] && sizeFocused !== prefix && (
                        <p className="flex items-start gap-1 text-xs text-destructive" data-testid={`tyre-size-${prefix}-string-error`}>
                          <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" />
                          <span>{parseErrors[prefix]} The fields below still hold the last valid size.</span>
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {TYRE_FIELD_SPECS.map((spec) => {
                        const key = `${prefix}${spec.key}`;
                        const id = `tyre-size-${key}`;
                        const isActive = active && active.tyre === prefix && active.part === spec.part;
                        return (
                          <div key={key} className="space-y-1.5 min-w-0"
                            onMouseEnter={() => onPart('hover', prefix, spec.part)} onMouseLeave={() => onPart('leave', prefix, spec.part)}>
                            <Label htmlFor={id} className={`font-mono text-[11px] uppercase tracking-wider ${isActive ? 'text-foreground font-bold underline decoration-2 underline-offset-4' : 'text-muted-foreground'}`}>
                              {FIELD_SHORT_LABEL[spec.key]} <span className="normal-case text-muted-foreground/70">({spec.unit})</span>
                            </Label>
                            <Input
                              id={id}
                              type="number"
                              step={spec.step}
                              min={spec.min}
                              max={spec.max}
                              inputMode="decimal"
                              value={tyres[prefix][FIELD_PROP[spec.key]]}
                              onChange={(e) => setTyreField(prefix, FIELD_PROP[spec.key], e.target.value)}
                              onFocus={() => onPart('focus', prefix, spec.part)}
                              onBlur={() => onPart('blur', prefix, spec.part)}
                              className={`font-mono px-2 ${isActive ? 'ring-2 ring-[hsl(var(--tyre-hl))]' : ''}`}
                              aria-invalid={!!fieldErrors[key]}
                              data-testid={`${id}-input`}
                            />
                            <FieldError error={fieldErrors[key]} testId={`${id}-error`} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-muted/50 rounded-md space-y-4">
                <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">Speedometer &amp; Radius</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <FieldLabel
                      htmlFor="tyre-size-radius-basis"
                      tip="A loaded, rolling tyre's effective radius is a few % smaller than its unloaded geometric radius, because the tread flattens at the contact patch. Dynamic uses radius = diameter ÷ 2 × factor (typically 0.96–0.98). The speedometer error is the same either way, since the factor applies to both tyres; it changes the rolling radius, revolutions per km and RPM."
                    >
                      Rolling-radius basis
                    </FieldLabel>
                    <Select value={radiusBasis} onValueChange={setRadiusBasis}>
                      <SelectTrigger id="tyre-size-radius-basis" data-testid="tyre-size-radius-basis-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="geometric">Geometric (unloaded)</SelectItem>
                        <SelectItem value="dynamic">Dynamic rolling radius</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {radiusBasis === 'dynamic'
                    ? globalField('dynamicFactor', { spec: DYNAMIC_FACTOR_SPEC, label: 'Dynamic factor', tip: 'Loaded rolling radius ÷ unloaded radius. 0.97 is a typical passenger-car value; range 0.93–1.00.' })
                    : <div className="hidden sm:block" />}
                  {globalField('speedoOverReadPct', {
                    label: 'Factory over-read',
                    tip: 'How much the original speedometer already reads high on the original tyres. Most speedometers read a few % high from the factory, because UNECE R39 forbids them from ever showing less than the true speed. Leave at 0 if unknown.',
                  })}
                  {globalField('cruiseSpeedKmph', { label: 'Cruise speed', tip: 'Used twice: as the speedometer reading for the gauge (what is the true speed when it shows this?), and as the true road speed for the RPM comparison (at the same indicated speed the wheels turn equally fast on both tyres, so RPM only differs at the same true speed).' })}
                  {globalField('annualDistanceKm', { label: 'Annual distance', tip: 'Distance you drive per year, as shown on the odometer. Used for the yearly odometer error.' })}
                  <div className="space-y-2">
                    <FieldLabel htmlFor="tyre-size-speed-unit" tip="Display unit for every speed result, the table and the chart. Inputs stay in km/h.">Speed display</FieldLabel>
                    <Select value={speedUnit} onValueChange={setSpeedUnit}>
                      <SelectTrigger id="tyre-size-speed-unit" data-testid="tyre-size-speed-unit-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kmph">km/h</SelectItem>
                        <SelectItem value="mph">mph</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-muted/50 rounded-md space-y-4">
                <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">Engine / Motor RPM (optional)</h3>
                <p className="text-xs text-muted-foreground">Enter both to compare RPM at the cruise speed on each tyre and get the final drive that would cancel the change.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {globalField('gearRatio', { optional: true, placeholder: 'e.g. 1.00', tip: 'Transmission ratio of the gear you cruise in (use 1.00 for a single-speed EV reduction and put the whole reduction in Final Drive).' })}
                  {globalField('finalDriveRatio', { optional: true, placeholder: 'e.g. 3.73' })}
                </div>
              </div>

              <div className="flex gap-2">
                <Button type="button" onClick={runCalculation} disabled={hasErrors} data-testid="tyre-size-calculate-btn">
                  <Calculator className="h-4 w-4" /> Calculate
                </Button>
                <Button type="button" variant="outline" onClick={handleReset} data-testid="tyre-size-reset-btn">
                  <RotateCcw className="h-4 w-4" /> Reset
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="lg:col-span-2 space-y-4">
            <SummaryCard hasErrors={hasErrors} calcError={calcError} results={results} speedUnit={speedUnit} />
          </div>
        </div>

        {results && !hasErrors && (
          <>
            <Card className="technical-card" data-testid="tyre-size-diagrams">
              <CardHeader>
                <CardTitle className="font-chivo uppercase tracking-tight text-sm flex items-center gap-2"><Ruler className="h-4 w-4 text-autoxium-blue" /> Tyre Diagrams (to scale)</CardTitle>
                <CardDescription>
                  Original dashed, new solid, both at the same scale on one ground line. The cross-section is aligned at the rim seat and
                  its rim width is illustrative. Tap or hover a part of either diagram to highlight it.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid lg:grid-cols-2 gap-6">
                  <div className="min-w-0">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Side view</p>
                    <SideView original={results.original} next={results.new} active={active} onPart={onPart}
                      clearanceChangeMm={results.comparison.groundClearanceChangeMm} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Cross-section (front)</p>
                    <CrossSection original={results.original} next={results.new} active={active} onPart={onPart} />
                  </div>
                </div>
                <div role="status" aria-live="polite" className="min-h-[3rem] rounded-md border border-dashed p-3 text-sm" data-testid="tyre-size-part-explanation">
                  {active ? (
                    <p>
                      <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground block mb-0.5">
                        {active.tyre === 'orig' ? 'Original' : 'New'} tyre · {PART_TITLES[active.part]}
                      </span>
                      <span className="font-semibold">{explainPart(active.part, tyres[active.tyre])}</span>
                    </p>
                  ) : (
                    <p className="text-muted-foreground">Hover, focus or tap a part of a size (e.g. <span className="font-mono">45</span>) or an input to see what each number means.</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <GaugeCard results={results} speedUnit={speedUnit} />
              <OdometerDrivelineCard results={results} />
            </div>

            <ComparisonTable results={results} />
            <SpeedoTable results={results} speedUnit={speedUnit} />
            <SpeedChart results={results} speedUnit={speedUnit} />
          </>
        )}
      </div>
    </TooltipProvider>
  );
};

// ---------------------------------------------------------------------------
// Size-string segment chips (hover / focus / tap to highlight)
// ---------------------------------------------------------------------------

const SegmentChips = ({ prefix, tyre, active, onPart }) => {
  const segments = sizeSegments(tyre);
  return (
    <div id={`tyre-size-${prefix}-segments`} className="flex flex-wrap items-center gap-0.5 font-mono text-sm" data-testid={`tyre-size-${prefix}-segments`}>
      <span className="sr-only">Size parts:</span>
      {segments.map((seg, i) => {
        if (!seg.part) return <span key={i} aria-hidden="true" className="text-muted-foreground px-px">{seg.text === ' ' ? ' ' : seg.text}</span>;
        const isActive = active && active.tyre === prefix && active.part === seg.part;
        return (
          <button
            key={i}
            type="button"
            className={`px-1.5 py-0.5 rounded border transition-colors ${isActive
              ? 'bg-[hsl(var(--tyre-hl)/0.2)] border-[hsl(var(--tyre-hl))] font-bold underline decoration-2 underline-offset-2'
              : 'border-transparent hover:border-border hover:bg-background'}`}
            aria-pressed={!!isActive}
            aria-label={`${seg.text}: ${PART_TITLES[seg.part]}`}
            onMouseEnter={() => onPart('hover', prefix, seg.part)}
            onMouseLeave={() => onPart('leave', prefix, seg.part)}
            onFocus={() => onPart('focus', prefix, seg.part)}
            onBlur={() => onPart('blur', prefix, seg.part)}
            onClick={() => onPart('tap', prefix, seg.part)}
            data-testid={`tyre-size-${prefix}-seg-${seg.part}`}
          >
            {seg.text}
          </button>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Summary
// ---------------------------------------------------------------------------

const Kpi = ({ label, value, unit, sub, testId, accent }) => (
  <div className="p-3 bg-muted/50 rounded-md min-w-0" data-testid={testId}>
    <p className="text-muted-foreground uppercase tracking-wider text-[10px] font-mono mb-1">{label}</p>
    <p className={`font-mono text-lg font-black break-words ${accent ? 'text-autoxium-blue' : 'text-foreground'}`}>
      {value} <span className="text-xs font-normal text-muted-foreground">{unit}</span>
    </p>
    {sub && <p className="text-[11px] text-muted-foreground font-mono">{sub}</p>}
  </div>
);

const R39Banner = ({ results, speedUnit }) => {
  const u = unitLabel(speedUnit);
  const { cruise, r39 } = results;
  if (r39.underReads) {
    return (
      <div className="flex items-start gap-2 rounded-md border-2 border-destructive bg-destructive/10 p-3 text-sm" data-testid="tyre-size-r39-banner" data-status="fail">
        <XCircle className="h-5 w-5 shrink-0 text-destructive mt-0.5" />
        <div>
          <p className="font-bold uppercase tracking-wide">R39 FAIL: speedometer under-reads</p>
          <p className="text-xs mt-1">
            With the new tyres, an indicated {fmt(toUnit(cruise.indicatedKmph, speedUnit), 0)} {u} is really{' '}
            <span className="font-mono font-bold">{fmt(toUnit(cruise.trueKmph, speedUnit), 1)} {u}</span>. UNECE R39 requires the indicated speed never to be below the true speed.
          </p>
        </div>
      </div>
    );
  }
  if (r39.anyFail) {
    return (
      <div className="flex items-start gap-2 rounded-md border-2 border-destructive bg-destructive/10 p-3 text-sm" data-testid="tyre-size-r39-banner" data-status="fail">
        <XCircle className="h-5 w-5 shrink-0 text-destructive mt-0.5" />
        <div>
          <p className="font-bold uppercase tracking-wide">R39 FAIL: over-reads too much</p>
          <p className="text-xs mt-1">The speedometer reads more than 10 % + 4 km/h above the true speed at some speeds. See the table below.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 rounded-md border-2 border-emerald-600/60 bg-emerald-600/10 p-3 text-sm" data-testid="tyre-size-r39-banner" data-status="pass">
      <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-700 mt-0.5" />
      <div>
        <p className="font-bold uppercase tracking-wide">R39 PASS</p>
        <p className="text-xs mt-1">The speedometer never shows less than the true speed and stays within 10 % + 4 km/h at every table speed.</p>
      </div>
    </div>
  );
};

const SummaryCard = ({ hasErrors, calcError, results, speedUnit }) => {
  const u = unitLabel(speedUnit);
  let body;
  if (calcError) {
    body = <div className="flex items-center gap-2 text-sm text-destructive" data-testid="tyre-size-calc-error"><AlertCircle className="h-4 w-4 shrink-0" /> {calcError}</div>;
  } else if (hasErrors) {
    body = <p className="text-sm text-muted-foreground" data-testid="tyre-size-results-placeholder">Fix the highlighted fields to see results.</p>;
  } else if (!results) {
    body = <p className="text-sm text-muted-foreground" data-testid="tyre-size-results-loading">Calculating…</p>;
  } else {
    const c = results.comparison;
    const err = results.speedoErrorPct;
    const speedRpm = speedRpmPrefillUrl(results.new.rollingRadiusMm);
    const gearRatio = gearRatioPrefillUrl({ widthMm: results.new.widthMm, aspectPct: results.new.aspectPct, rimIn: results.new.rimIn });
    body = (
      <div className="space-y-3">
        <R39Banner results={results} speedUnit={speedUnit} />
        <div className="grid grid-cols-2 gap-3">
          <Kpi label="Diameter change" value={fmtSigned(c.diameterDiffPct, 2)} unit="%" sub={`${fmtSigned(c.diameterDiffMm, 1)} mm`} testId="tyre-size-kpi-diameter" accent />
          <Kpi label="Speedometer error" value={fmtSigned(err, 2)} unit="%" sub={err < 0 ? 'reads low' : err > 0 ? 'reads high' : 'exact'} testId="tyre-size-kpi-speedo" accent />
          <Kpi label={`True @ ${fmt(toUnit(results.cruise.indicatedKmph, speedUnit), 0)} shown`} value={fmt(toUnit(results.cruise.trueKmph, speedUnit), 1)} unit={u} testId="tyre-size-kpi-true" />
          <Kpi label="Ride height" value={fmtSigned(c.groundClearanceChangeMm, 1)} unit="mm" sub="Δ diameter ÷ 2" testId="tyre-size-kpi-clearance" />
          <Kpi label={results.radiusBasis === 'dynamic' ? 'New rolling radius' : 'New radius'} value={fmt(results.new.rollingRadiusMm, 1)} unit="mm" testId="tyre-size-kpi-radius" />
          <Kpi label="Revs per km" value={fmt(results.new.revsPerKm, 1)} unit="" sub={`was ${fmt(results.original.revsPerKm, 1)}`} testId="tyre-size-kpi-revs" />
        </div>
        {results.warnings.length > 0 && (
          <ul className="space-y-2" data-testid="tyre-size-warnings">
            {results.warnings.map((w) => (
              <li key={w.code} className="flex items-start gap-1.5 text-xs text-amber-900 bg-amber-500/10 border border-amber-500/40 rounded-md p-2.5" data-testid={`tyre-size-warning-${w.code}`}>
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>
                  {w.code === 'diameterRuleOfThumb' && <span className="font-bold">Rule of thumb (not a standard): </span>}
                  {w.message}
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="space-y-2 pt-1 border-t border-structure">
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground pt-2">Use the new tyre in</p>
          <CrossToolButton to={speedRpm.url} disabledReason={speedRpm.disabledReason} testId="tyre-size-to-speed-rpm">Vehicle Speed → RPM</CrossToolButton>
          <CrossToolButton to={gearRatio.url} disabledReason={gearRatio.disabledReason} testId="tyre-size-to-gear-ratio">Gear Ratio Calculator</CrossToolButton>
        </div>
      </div>
    );
  }
  return (
    <Card className="technical-card ring-1 ring-autoxium-blue/30 h-fit" data-testid="tyre-size-results">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-base">Summary</CardTitle>
      </CardHeader>
      <CardContent><div role="status" aria-live="polite">{body}</div></CardContent>
    </Card>
  );
};

const CrossToolButton = ({ to, disabledReason, testId, children }) => (
  <div>
    {disabledReason ? (
      <Button type="button" variant="outline" size="sm" className="w-full justify-between" disabled data-testid={testId}>
        {children} <ArrowRight className="h-3.5 w-3.5" />
      </Button>
    ) : (
      <Button asChild variant="outline" size="sm" className="w-full justify-between">
        <Link to={to} data-testid={testId}>{children} <ArrowRight className="h-3.5 w-3.5" /></Link>
      </Button>
    )}
    {disabledReason && <p className="text-[11px] text-muted-foreground mt-1">{disabledReason}</p>}
  </div>
);

// ---------------------------------------------------------------------------
// Gauge, odometer & driveline
// ---------------------------------------------------------------------------

const GaugeCard = ({ results, speedUnit }) => {
  const u = unitLabel(speedUnit);
  const { cruise } = results;
  const diff = toUnit(cruise.trueKmph - cruise.indicatedKmph, speedUnit);
  return (
    <Card className="technical-card">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-sm flex items-center gap-2"><Gauge className="h-4 w-4 text-autoxium-blue" /> Speedometer at Cruise</CardTitle>
        <CardDescription>Thick grey needle: what the speedometer shows. Dashed blue needle: your true speed on the new tyres. The Δ strip magnifies the gap.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <SpeedoGauge indicatedKmph={cruise.indicatedKmph} trueKmph={cruise.trueKmph} unit={speedUnit} />
        <p className="text-sm text-center font-mono" data-testid="tyre-size-gauge-text">
          Shows <span className="font-bold">{fmt(toUnit(cruise.indicatedKmph, speedUnit), 1)}</span> · True{' '}
          <span className="font-bold text-autoxium-blue">{fmt(toUnit(cruise.trueKmph, speedUnit), 1)}</span> {u}
          <span className="block text-xs text-muted-foreground">
            {Math.abs(diff) < 0.005 ? 'No difference' : `You are going ${fmt(Math.abs(diff), 2)} ${u} ${diff > 0 ? 'faster' : 'slower'} than shown`}
          </span>
        </p>
      </CardContent>
    </Card>
  );
};

const OdometerDrivelineCard = ({ results }) => {
  const { per10000Km, perYear } = results.odometer;
  const d = results.driveline;
  const under = per10000Km.differenceKm > 0;
  return (
    <Card className="technical-card">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-sm">Odometer &amp; RPM</CardTitle>
        <CardDescription>Odometer figures use the odometer reading as the base: actual distance = reading × speed ratio ({fmt(results.speedRatio, 5)}).</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-3">
          <Kpi label="Per 10,000 km shown" value={fmt(per10000Km.actualKm, 0)} unit="km actual" sub={`${fmtSigned(per10000Km.differenceKm, 0)} km ${under ? 'not recorded' : 'over-recorded'}`} testId="tyre-size-kpi-odo-10k" />
          <Kpi label={`Per year (${fmt(perYear.odometerKm, 0)} km shown)`} value={fmt(perYear.actualKm, 0)} unit="km actual" sub={`${fmtSigned(perYear.differenceKm, 0)} km ${perYear.differenceKm > 0 ? 'not recorded' : 'over-recorded'}`} testId="tyre-size-kpi-odo-year" />
        </div>
        {d ? (
          <div className="grid grid-cols-2 gap-3" data-testid="tyre-size-driveline">
            <Kpi label={`RPM @ ${fmt(d.cruiseSpeedKmph, 0)} km/h true`} value={fmt(d.rpmNew, 0)} unit="rpm" sub={`was ${fmt(d.rpmOriginal, 0)} (${fmtSigned(d.rpmDiffPct, 2)} %)`} testId="tyre-size-kpi-rpm" />
            <Kpi label="Equivalent final drive" value={fmt(d.equivalentFinalDrive, 3)} unit=":1" sub={`from ${fmt(d.finalDriveRatio, 2)} × ${fmt(results.speedRatio, 4)}`} testId="tyre-size-kpi-eq-fd" accent />
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Enter a gear ratio and final drive ratio above to compare engine/motor RPM and see the final drive that restores the original RPM-to-speed relationship.</p>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

const ComparisonTable = ({ results }) => {
  const o = results.original;
  const n = results.new;
  const liText = (t) => (t.loadIndex ? `${t.loadIndex.index} (${fmt(t.loadIndex.kg, 0)} kg)` : '—');
  const ssText = (t) => {
    const s = t.speedSymbol;
    if (!s) return '—';
    return `${s.symbol ?? 'ZR'} (${s.above ? '>' : '≤'} ${s.kmph} km/h)`;
  };
  const rows = [
    ['Size', o.sizeLabel, n.sizeLabel, ''],
    ['Section width', `${fmt(o.widthMm, 0)} mm`, `${fmt(n.widthMm, 0)} mm`, `${fmtSigned(results.comparison.widthDiffMm, 0)} mm`],
    ['Aspect ratio', `${fmt(o.aspectPct, 0)} %`, `${fmt(n.aspectPct, 0)} %`, ''],
    ['Sidewall height', `${fmt(o.sidewallMm, 1)} mm`, `${fmt(n.sidewallMm, 1)} mm`, `${fmtSigned(results.comparison.sidewallDiffMm, 1)} mm`],
    ['Rim diameter', `${o.rimIn}″ (${fmt(o.rimDiameterMm, 1)} mm)`, `${n.rimIn}″ (${fmt(n.rimDiameterMm, 1)} mm)`, `${fmtSigned(results.comparison.rimDiffIn, 1)}″`],
    ['Overall diameter', `${fmt(o.diameterMm, 1)} mm`, `${fmt(n.diameterMm, 1)} mm`, `${fmtSigned(results.comparison.diameterDiffMm, 1)} mm (${fmtSigned(results.comparison.diameterDiffPct, 2)} %)`],
    ['Circumference', `${fmt(o.circumferenceMm, 0)} mm`, `${fmt(n.circumferenceMm, 0)} mm`, ''],
    [results.radiusBasis === 'dynamic' ? `Rolling radius (× ${results.rollingFactor})` : 'Radius (geometric)', `${fmt(o.rollingRadiusMm, 1)} mm`, `${fmt(n.rollingRadiusMm, 1)} mm`, `${fmtSigned(results.comparison.rollingRadiusDiffMm, 1)} mm`],
    ['Revolutions per km', fmt(o.revsPerKm, 1), fmt(n.revsPerKm, 1), fmtSigned(results.comparison.revsPerKmDiff, 1)],
    ['Load index', liText(o), liText(n), ''],
    ['Speed symbol', ssText(o), ssText(n), ''],
  ];
  return (
    <Card className="technical-card">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-sm">Tyre Comparison</CardTitle>
      </CardHeader>
      <CardContent>
        <Table className="text-xs font-mono min-w-[560px]" data-testid="tyre-size-comparison-table">
          <TableHeader>
            <TableRow className="text-muted-foreground uppercase tracking-wider text-[10px] hover:bg-transparent">
              <TableHead className="text-left h-auto py-2 pr-3">Parameter</TableHead>
              <TableHead className="text-right h-auto py-2 pr-3">Original</TableHead>
              <TableHead className="text-right h-auto py-2 pr-3">New</TableHead>
              <TableHead className="text-right h-auto py-2">Change</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(([label, a, b, delta]) => (
              <TableRow key={label}>
                <TableCell className="py-2 pr-3 font-bold font-manrope">{label}</TableCell>
                <TableCell className="text-right pr-3">{a}</TableCell>
                <TableCell className="text-right pr-3 text-autoxium-blue font-bold">{b}</TableCell>
                <TableCell className="text-right">{delta}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

const SpeedoTable = ({ results, speedUnit }) => {
  const u = unitLabel(speedUnit);
  const rows = results.speedoTable[speedUnit];
  return (
    <Card className="technical-card">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-sm">Speedometer Error Table</CardTitle>
        <CardDescription>
          True speed = indicated × speed ratio ÷ (1 + factory over-read). UNECE R39 para 5.4: 0 ≤ indicated − true ≤ 0.1 × true + 4 km/h.
          Positive difference = the speedometer reads high.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table className="text-xs font-mono min-w-[560px]" data-testid="tyre-size-speedo-table">
          <TableHeader>
            <TableRow className="text-muted-foreground uppercase tracking-wider text-[10px] hover:bg-transparent">
              <TableHead className="text-left h-auto py-2 pr-3">Indicated ({u})</TableHead>
              <TableHead className="text-right h-auto py-2 pr-3">True ({u})</TableHead>
              <TableHead className="text-right h-auto py-2 pr-3">Difference ({u})</TableHead>
              <TableHead className="text-right h-auto py-2 pr-3">Error</TableHead>
              <TableHead className="text-right h-auto py-2">UNECE R39</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const indicated = speedUnit === 'mph' ? r.indicatedMph : r.indicatedKmph;
              return (
                <TableRow key={indicated} data-testid={`tyre-size-speedo-row-${indicated}`} className={r.r39Pass ? '' : 'bg-destructive/5'}>
                  <TableCell className="py-2 pr-3 font-bold">{fmt(indicated, 0)}</TableCell>
                  <TableCell className="text-right pr-3">{fmt(toUnit(r.trueKmph, speedUnit), 1)}</TableCell>
                  <TableCell className="text-right pr-3">{fmtSigned(toUnit(r.differenceKmph, speedUnit), 2)}</TableCell>
                  <TableCell className="text-right pr-3">{fmtSigned(r.errorPct, 2)} %</TableCell>
                  <TableCell className="text-right">
                    {r.r39Pass ? (
                      <Badge variant="outline" className="border-emerald-600/60 text-emerald-800 gap-1"><CheckCircle2 className="h-3 w-3" /> PASS</Badge>
                    ) : (
                      <Badge variant="outline" className="border-destructive text-destructive gap-1" title={r.r39Reason}><XCircle className="h-3 w-3" /> FAIL · {r.r39Reason}</Badge>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Chart
// ---------------------------------------------------------------------------

const SpeedChart = ({ results, speedUnit }) => {
  const u = unitLabel(speedUnit);
  const zoom = useChartZoom([0, 'dataMax']);
  const data = useMemo(() => results.chart.map((p) => {
    const x = Math.round(toUnit(p.indicatedKmph, speedUnit) * 100) / 100;
    return {
      indicated: x,
      ideal: x,
      trueSpeed: Math.round(toUnit(p.trueKmph, speedUnit) * 100) / 100,
      band: [Math.round(toUnit(p.bandLowKmph, speedUnit) * 100) / 100, Math.round(toUnit(p.bandHighKmph, speedUnit) * 100) / 100],
    };
  }), [results, speedUnit]);

  return (
    <Card className="technical-card">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-sm">Indicated vs. True Speed</CardTitle>
        <CardDescription>
          The shaded band is where UNECE R39 allows the true speed to be for each indicated speed. A new-tyre line above the dashed
          ideal line means the speedometer under-reads. Drag across the chart to zoom in.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ChartZoomHeader isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-tyre-size" />
        <div className="h-80 md:h-96" data-testid="tyre-size-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 24 }}
              onMouseDown={zoom.onMouseDown} onMouseMove={zoom.onMouseMove} onMouseUp={zoom.onMouseUp}>
              <CartesianGrid stroke={CHART_GRID} vertical={false} />
              <XAxis dataKey="indicated" type="number" domain={zoom.xDomain} allowDataOverflow tick={AXIS_TICK_STYLE}
                label={{ value: `Indicated speed (${u})`, position: 'insideBottom', offset: -12, style: AXIS_LABEL_STYLE }} />
              <YAxis tick={AXIS_TICK_STYLE} width={56} domain={['auto', 'auto']}
                label={{ value: `True speed (${u})`, angle: -90, position: 'insideLeft', style: AXIS_LABEL_STYLE }} />
              <RechartsTooltip
                contentStyle={TOOLTIP_CONTENT_STYLE}
                labelStyle={TOOLTIP_LABEL_STYLE}
                labelFormatter={(v) => `Indicated ${fmt(v, 1)} ${u}`}
                formatter={(v, name) => (Array.isArray(v) ? [`${fmt(v[0], 1)} – ${fmt(v[1], 1)} ${u}`, name] : [`${fmt(v, 2)} ${u}`, name])}
              />
              <Legend wrapperStyle={{ ...LEGEND_STYLE, paddingTop: 18 }} />
              <Area type="linear" dataKey="band" name="R39 allowed true speed" stroke="none" fill={SERIES_COLORS[2]} fillOpacity={0.18} isAnimationActive={false} />
              <Line type="linear" dataKey="ideal" name="Ideal (reads true)" stroke={CHART_AXIS} strokeDasharray="5 4" {...LINE_PROPS} strokeWidth={1.5} />
              <Line type="linear" dataKey="trueSpeed" name="New tyre: true speed" stroke={SERIES_COLORS[0]} {...LINE_PROPS} />
              {zoom.isSelecting && (
                <ReferenceArea x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default TyreSizeCalculator;
